import {Component, HostListener} from '@angular/core';
import {isNil} from 'lodash';
import {hasAccountBeenActivated, PortfolioAccount} from 'src/app/domain/model/portfolio/portfolio-account.model';
import PortfolioAccountApi from 'src/app/domain/service/api/portfolio-account.api';
import {PremiumModalService} from 'src/app/domain/service/premium-customer/premium-modal.service';
import CustomerPortfolioApi from 'src/app/domain/service/api/customer-portfolio.api';
import CustomerApi from 'src/app/domain/service/api/customer/customer.api';
import UserApi from 'src/app/domain/service/api/user.api';
import ProductRouteResolver from 'src/app/domain/service/route-resolver/product-route-resolver.service';
import RouteResolverService from 'src/app/domain/service/route-resolver/route-resolver.service';
import {AppEnvironmentService} from 'src/app/infrastructure/environment/app-environment/app-environment.service';
import {User} from 'src/app/domain/model/user.model';
import {
  CustomerPortfolio, isCanceledPortfolio,
  isEnabledPortfolio,
  isInvestmentPortfolio,
  isPensionPlanPortfolio,
  isPremiumPortfolio
} from 'src/app/domain/model/customer-portfolio.model';
import {WebComponent} from 'src/app/ui/webapp/component/web.component';
import {
  Customer,
  hasTotalOperationalBlockReasons,
  isPhysicalCustomer,
  isPremiumCustomer
} from 'src/app/domain/model/customer/customer.model';
import CustomerAreaRouteResolver from 'src/app/domain/service/route-resolver/customer-area-route-resolver.service';
import {environment} from 'src/environments/environment';
import BackofficePortfolioApi from 'src/app/domain/service/api/backoffice-portfolio.api';
import {BackofficePortfolio} from 'src/app/domain/model/backoffice-portfolio/backoffice-portfolio.model';
import {Router} from 'src/app/infrastructure/router/router';
import {getStrategyIconByName, isAssetIcon} from 'src/app/domain/model/investment-strategy/investment-strategy.model';
import {ThemeService} from 'src/app/domain/service/theme/theme.service';
import ResumeSlideStore from 'src/app/infrastructure/store/resume-slide.store';

type Type = 'normal' | 'tiny';

@Component({
  selector: 'app-customer-area-menu',
  templateUrl: 'menu.component.html',
  host: {
    class: 'h-full w-full'
  },
})
export class MenuComponent extends WebComponent {
  public type: Type;
  public loggedUser: User;
  public customer: Customer;
  public account: PortfolioAccount;
  public selectedPortfolio: CustomerPortfolio | null;
  public resumeUrl: string;
  public showShareScreen = false;
  public selectedBoPortfolio: BackofficePortfolio | null = null;
  public profileDropdownOpen = false;
  public newProductSlide = false;

  private accountSubscriptionId: string = null;
  private boPortfolioSubscriptionId: string = null;

  constructor(
    private readonly appEnvironment: AppEnvironmentService,
    private readonly userApi: UserApi,
    private readonly cuPortfolioApi: CustomerPortfolioApi,
    private readonly customerApi: CustomerApi,
    private readonly portfolioAccountApi: PortfolioAccountApi,
    private readonly productRouteResolver: ProductRouteResolver,
    private readonly customerAreaRouteResolver: CustomerAreaRouteResolver,
    private readonly routeResolver: RouteResolverService,
    private readonly premiumModal: PremiumModalService,
    private readonly backofficePortfolioApi: BackofficePortfolioApi,
    private readonly router: Router,
    private readonly resumeSlideStore: ResumeSlideStore,
    public readonly themeService: ThemeService
  ) {
    super();
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.profile-dropdown-container')) {
      this.profileDropdownOpen = false;
    }
  }

  public isSelectedPortfolioEnabled(): boolean {
    return this.hasSelectedPortfolio() && isEnabledPortfolio(this.selectedPortfolio);
  }

  public hasSelectedPortfolio(): boolean {
    return this.selectedPortfolio !== null;
  }

  public canShowDeposit(): boolean {
    return this.hasSelectedPortfolio()
      && !isCanceledPortfolio(this.selectedPortfolio)
      && !isInvestmentPortfolio(this.selectedPortfolio);
  }

  public canShowExtraordinaryCashTransferIn(): boolean {
    return this.hasSelectedPortfolio()
      && !isCanceledPortfolio(this.selectedPortfolio)
      && isInvestmentPortfolio(this.selectedPortfolio)
      && hasAccountBeenActivated(this.account);
  }

  public canShowRegularCashTransferIn(): boolean {
    return this.hasSelectedPortfolio()
      && !isCanceledPortfolio(this.selectedPortfolio)
      && isInvestmentPortfolio(this.selectedPortfolio)
      && hasAccountBeenActivated(this.account);
  }

  public canShowWithdrawal(): boolean {
    return this.isSelectedPortfolioEnabled();
  }

  public canShowPortfolioConfiguration(): boolean {
    return this.hasSelectedPortfolio();
  }

  public canShowPortfolioConfigurationButton(): boolean {
    return this.canShowPortfolioConfiguration() && !isCanceledPortfolio(this.selectedPortfolio);
  }

  public canShowInvestmentTransferFundsButton(): boolean {
    return this.customer !== null
      && isPhysicalCustomer(this.customer)
      && hasAccountBeenActivated(this.account)
      && isInvestmentPortfolio(this.selectedPortfolio);
  }

  public canShowPensionPlanTransferButton(): boolean {
    return this.hasSelectedPortfolio()
      && isPensionPlanPortfolio(this.selectedPortfolio)
      && !isCanceledPortfolio(this.selectedPortfolio);
  }

  public premiumCustomer(): boolean {
    return isPremiumCustomer(this.customer);
  }

  public canShowPremiumButton(): boolean {
    return !isPremiumCustomer(this.customer)
      && this.isSelectedPortfolioEnabled()
      && !isPremiumPortfolio(this.selectedPortfolio)
      && isInvestmentPortfolio(this.selectedPortfolio);
  }

  public canShowRecommendUsButton(): boolean {
    return !this.appEnvironment.isAppForThirdPartners();
  }

  public canShowContributionsGroup(): boolean {
    return (this.canShowRegularCashTransferIn() || this.canShowExtraordinaryCashTransferIn())
      && !this.hasCustomerOperationalBlockReasons();
  }

  public async showPremiumModal(): Promise<void> {
    await this.premiumModal.present();
  }

  public canShowPoweredBy(): boolean {
    return this.appEnvironment.isAppForThirdPartners();
  }

  public getUserInitials(): string {
    const name = (this.loggedUser?.userData?.name ?? '').trim();
    const surname = (this.loggedUser?.userData?.firstSurname ?? '').trim();
    const first = name.charAt(0).toUpperCase();
    const last = surname.charAt(0).toUpperCase();
    return (first + last) || '?';
  }

  public depositUrl(): string {
    return this.productRouteResolver.resolveAddMoneyUrl(this.selectedPortfolio);
  }

  public extraordinaryCashTransferInUrl(): string {
    return this.productRouteResolver.resolveAddMoneyUrl(this.selectedPortfolio);
  }

  public regularCashTransferInUrl(): string {
    return this.productRouteResolver.resolveAddMoneyUrl(this.selectedPortfolio, true);
  }

  public investmentTransferFundsUrl(): string {
    return this.productRouteResolver.resolveInvestmentTransferFunds();
  }

  public allPortfoliosUrl(): string {
    return this.customerAreaRouteResolver.resolveAllPortfolios();
  }

  public amountWithoutFees(): number {
    return this.appEnvironment.investmentPlanFreeAmountPerCustomerAdded();
  }

  public isRecommendUsButtonOfTypeFriendPlan(): boolean {
    return isInvestmentPortfolio(this.selectedPortfolio);
  }

  public showPoweredBy(): boolean {
    return environment.layout.showPoweredBy;
  }

  public shouldShowLink(): boolean {
    return environment.layout.showLink;
  }

  public shouldShowButtonLogout(): boolean {
    return this.appEnvironment.shouldShowLogout();
  }

  public shouldShowFeesMenuButton(): boolean {
    return this.isSelectedPortfolioEnabled();
  }

  public hasCustomerOperationalBlockReasons(): boolean {
    return hasTotalOperationalBlockReasons(this.customer);
  }

  public isNewProductScreen(): boolean {
    const currentUrl = this.router.currentUrl().url;
    return currentUrl.startsWith('/client/plan/create')
      || (this.newProductSlide && currentUrl === this.customerAreaRouteResolver.resolveRoot());
  }

  public redirectRouteFeesPage(): string {
    return this.customerAreaRouteResolver.resolveFeePage();
  }

  public allowSecondContract(): boolean {
    return this.appEnvironment.allowSecondContract();
  }

  public clickOnLogo(): void {
    this.router.navigate([this.resumeUrl]);
  }

  public doPartnerProvidesUserData(): boolean {
    return AppEnvironmentService.doPartnerProvidesUserData();
  }

  public toggleProfileDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.profileDropdownOpen = !this.profileDropdownOpen;
  }

  public toggleTheme(event: MouseEvent): void {
    event.stopPropagation();
    this.themeService.toggleTheme();
  }

  public getStrategyLogo(): string {
    const strategyName = this.selectedBoPortfolio?.investmentStrategy?.name;

    if (!strategyName) {
      return '';
    }

    return getStrategyIconByName(strategyName);
  }

  public isStrategyLogoAsset(): boolean {
    const strategyName = this.selectedBoPortfolio?.investmentStrategy?.name;

    if (!strategyName) {
      return false;
    }

    return isAssetIcon(strategyName);
  }

  protected onInit() {
    this.subscribe(this.router.changed(), (route) => {
      this.profileDropdownOpen = false;
    });

    this.subscribe(this.resumeSlideStore.newProduct$, (newProduct) => this.newProductSlide = newProduct);

    this.showShareScreen = this.appEnvironment.showShareScreen();
    this.subscribe(this.userApi.me(), (user) => {
      this.loggedUser = user;
      this.subscribe(this.customerApi.customer(), (customer) => this.customer = customer);
      this.subscribe(this.cuPortfolioApi.selected(), (portfolio) => {
        this.selectedPortfolio = portfolio;
        this.resumeUrl = this.routeResolver.resolve();
        this.selectedBoPortfolio = null;

        if (!isNil(this.selectedPortfolio)) {
          this.unsubscribe(this.accountSubscriptionId);
          this.unsubscribe(this.boPortfolioSubscriptionId);

          this.accountSubscriptionId = this.subscribe(
            this.portfolioAccountApi.byCustomerPortfolioId(this.selectedPortfolio.id),
            (account) => this.account = account
          );

          this.boPortfolioSubscriptionId = this.subscribe(
            this.backofficePortfolioApi.byId(this.selectedPortfolio.portfolioMaster),
            (boPortfolio) => this.selectedBoPortfolio = boPortfolio
          );
        }
      });
    });
  }
}
